import { useState, useEffect } from 'react';
import { apiFetch, formatDate, DPP_BASE_URL } from '../dppApi';

// Customer-facing bespoke tracker — a pinned section on My Collection, styled as an
// editorial "follow your commission" view (Option C). The design is the hero; progress
// is a quiet dot row; the customer sees only the high-level quoted price and can just
// Approve / Request changes / Message — no cost breakdown, specification, or internal
// drafts (see routes/bespoke.py _commission_dict view="customer").
// Four calm milestones for the customer, mapped straight to the piece's own state.
const DOTS = [
    { key: 'designing', label: 'Design' },
    { key: 'approved', label: 'Approved' },
    { key: 'in_production', label: 'Making' },
    { key: 'done', label: 'Ready' },
];
const revLabel = (s) => (s === 'approved' ? 'Approved' : s === 'changes_requested' ? 'Changes requested' : 'Shared for your approval');
const money = (n, cur) => (n == null ? '—' : `${cur || ''} ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`.trim());

const UserBespoke = ({ token, commissions }) => {
    const [list, setList] = useState(commissions || null);
    const [openId, setOpenId] = useState(null);
    const [c, setC] = useState(null);
    const [activePiece, setActivePiece] = useState(null);
    const [selectedRev, setSelectedRev] = useState(null);
    const [draft, setDraft] = useState('');
    const [changeFor, setChangeFor] = useState(null);   // revision_id being requested-changes on
    const [changeNote, setChangeNote] = useState('');
    const [err, setErr] = useState(null);

    useEffect(() => {
        // When the collection page already fetched the list, use it; otherwise fetch.
        if (commissions) { setList(commissions); return; }
        apiFetch('/api/user/commissions', { token })
            .then(d => setList(d.commissions || []))
            .catch(e => setErr(e.message));
    }, [token, commissions]);

    const open = (id) => apiFetch(`/api/user/commissions/${id}`, { token })
        .then(d => { setC(d); setOpenId(id); setActivePiece(d.pieces?.[0]?.piece_id || null); setSelectedRev(null); setChangeFor(null); })
        .catch(e => setErr(e.message));
    const back = () => { setOpenId(null); setC(null); };
    const approve = (rid) => apiFetch(`/api/user/commissions/${openId}/revisions/${rid}/approve`, { token, method: 'POST' })
        .then(setC).catch(e => setErr(e.message));
    const requestChanges = (rid) => apiFetch(`/api/user/commissions/${openId}/revisions/${rid}/request-changes`, { token, method: 'POST', body: { body: changeNote } })
        .then(d => { setC(d); setChangeFor(null); setChangeNote(''); }).catch(e => setErr(e.message));
    const send = () => {
        if (!draft.trim()) return;
        apiFetch(`/api/user/commissions/${openId}/messages`, { token, method: 'POST', body: { body: draft } })
            .then(d => { setC(d); setDraft(''); }).catch(e => setErr(e.message));
    };

    // Only surface accepted, non-cancelled commissions; hide the whole section otherwise.
    const active = (list || []).filter(x => x.lifecycle !== 'cancelled');
    if (!list || !active.length) return null;

    // ── list: the set of commissions, each showing every piece's image ──
    if (!openId) {
        return (
            <section className="ubsp">
                {err && <p className="dpp-error">{err}</p>}
                {active.map(x => (
                    <div className="ubsp-comm" key={x.commission_id} onClick={() => open(x.commission_id)}>
                        {x.status_label && (
                            <span className={`ubsp-status${x.awaiting_customer ? ' need' : ''}`}>{x.status_label}</span>
                        )}
                        <p className="ubsp-comm-kicker">
                            {x.brand_header_logo_url
                                ? <img className="ubsp-brand-logo" src={x.brand_header_logo_url} alt={x.brand_name || 'Brand'} />
                                : <span className="ubsp-brand-name" style={{ color: x.brand_text_color || x.brand_color || undefined }}>{x.brand_name}</span>}
                        </p>
                        <p className="ubsp-comm-nm">{x.title}</p>
                        <p className="ubsp-comm-mt">{x.piece_count} piece{x.piece_count === 1 ? '' : 's'}{x.target_delivery_date ? ` · Yours by ${formatDate(x.target_delivery_date)}` : ''}</p>
                        <div className="ubsp-strip">
                            {(x.pieces || []).map(p => (
                                <div className="ubsp-strip-piece" key={p.piece_id}>
                                    <div className="ubsp-strip-th">{p.image ? <img src={p.image} alt={p.name} /> : '◈'}</div>
                                    <div className="ubsp-strip-nm">{p.name}</div>
                                    {p.passport && (
                                        <a className="passport-link ubsp-strip-pass"
                                            href={`${DPP_BASE_URL}/jewellery/${p.passport.qr_code_id}`}
                                            target="_blank" rel="noreferrer"
                                            onClick={e => e.stopPropagation()}>Passport</a>
                                    )}
                                </div>
                            ))}
                        </div>
                        {x.quote_total != null && <div className="ubsp-comm-price">{money(x.quote_total, x.currency)}</div>}
                        {x.last_message && (
                            <div className="ubsp-lastmsg">
                                {x.last_message.from_brand && <span className="ubsp-unread" aria-hidden="true" />}
                                <span className="ubsp-lastmsg-txt">
                                    {x.last_message.actor === 'system'
                                        ? x.last_message.body
                                        : `${x.last_message.from_brand ? (x.brand_name || 'Brand') : 'You'}: ${x.last_message.body}`}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </section>
        );
    }

    // ── detail (editorial) ──
    if (!c) return null;
    const piece = c.pieces.find(p => p.piece_id === activePiece) || c.pieces[0];
    const cur = piece && (piece.revisions.find(r => r.revision_id === selectedRev)
        || piece.revisions.find(r => r.revision_id === piece.current_revision_id)
        || piece.revisions[piece.revisions.length - 1] || null);
    const awaiting = !!cur && cur.revision_id === piece.current_revision_id && cur.status === 'shared';
    const images = cur?.renders || [];
    const ci = piece ? DOTS.findIndex(d => d.key === piece.status) : -1;

    return (
        <section className="ubsp ubsp-detail">
            <span className="ubsp-back" onClick={back}>← Back to Commissions</span>
            {err && <p className="dpp-error">{err}</p>}

            {/* Commission summary card — from the "Bespoke commission" title through
                View Passport — grouped in its own div, titled by the kicker. */}
            <div className="ubsp-project">
                <p className="ubsp-kicker">Bespoke commission · {c.brand_name}{c.pieces.length > 1 ? ` · ${c.pieces.length} pieces` : ''}</p>
                <h2 className="ubsp-headline">{c.title}</h2>
                {c.created_at && <p className="ubsp-sub">Commissioned {formatDate(c.created_at)}</p>}

                {c.pieces.length > 1 && (
                    <div className="ubsp-switch">
                        {c.pieces.map(p => (
                            <button key={p.piece_id} className={p.piece_id === piece?.piece_id ? 'on' : ''}
                                onClick={() => { setActivePiece(p.piece_id); setSelectedRev(null); setChangeFor(null); }}>
                                {p.name}
                            </button>
                        ))}
                    </div>
                )}

                {piece && (
                    <>
                        <div className="ubsp-hero">{images[0] ? <img src={images[0].url} alt="" /> : <span className="ubsp-hero-ph">◈</span>}</div>

                        {piece.revisions.length > 1 && (
                            <div className="ubsp-versions">
                                {piece.revisions.map(r => (
                                    <span key={r.revision_id} className={r.revision_id === cur?.revision_id ? 'on' : ''}
                                        onClick={() => setSelectedRev(r.revision_id)}>v{r.version}</span>
                                ))}
                            </div>
                        )}
                        {cur && <p className="ubsp-vcap">Version {cur.version} · <b>{revLabel(cur.status)}</b></p>}

                        {awaiting && (
                            <div className="ubsp-action">
                                {changeFor === cur.revision_id ? (
                                    <>
                                        <textarea className="ubsp-note" rows={2} placeholder="Slightly narrower band, warmer emerald tone, etc."
                                            value={changeNote} onChange={e => setChangeNote(e.target.value)} />
                                        <div className="ubsp-actions">
                                            <button className="estate-btn estate-btn--primary" onClick={() => requestChanges(cur.revision_id)}>Send request</button>
                                            <button className="estate-btn estate-btn--secondary" onClick={() => setChangeFor(null)}>Cancel</button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="ubsp-actions">
                                        <button className="estate-btn estate-btn--primary" onClick={() => approve(cur.revision_id)}>Approve</button>
                                        <button className="estate-btn estate-btn--secondary" onClick={() => setChangeFor(cur.revision_id)}>Request changes</button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="ubsp-dots">
                            {DOTS.map((d, i) => (
                                <span key={d.key} className={`ubsp-dot${i < ci ? ' done' : ''}${i === ci ? ' now' : ''}`}>{d.label}</span>
                            ))}
                        </div>
                    </>
                )}

                {c.target_delivery_date && <p className="ubsp-target">Yours by · {formatDate(c.target_delivery_date)}</p>}
                {piece?.passport && (
                    <p className="ubsp-target">
                        <a className="passport-link" href={`${DPP_BASE_URL}/jewellery/${piece.passport.qr_code_id}`} target="_blank" rel="noreferrer">Passport</a>
                    </p>
                )}
            </div>

            {/* Conversation mirrors the brand dashboard: a bordered pane with a header,
                the message thread, and a composer along the bottom. */}
            <div className="bsp-pane bsp-pane--convo ubsp-convo">
                <div className="bsp-pane-h"><h3>Conversation</h3></div>
                <div className="bsp-thread">
                    {c.messages.map(m => (
                        m.actor === 'system'
                            ? <div className="bsp-sys" key={m.message_id}>{m.body}</div>
                            : <div className={`bsp-msg ${m.actor === 'customer' ? 'us' : 'them'}`} key={m.message_id}>
                                <div className="bsp-who">{m.actor === 'customer' ? 'You' : (c.brand_name || 'Brand')} · {m.created_at ? formatDate(m.created_at) : ''}</div>
                                <div className="bsp-bub">{m.body}</div>
                            </div>
                    ))}
                </div>
                <div className="bsp-composer">
                    <div className="bsp-cbox">
                        <textarea rows={1} value={draft} onChange={e => setDraft(e.target.value)} />
                        <button className="bsp-send" onClick={send} aria-label="Send">→</button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserBespoke;
